import React from "react";
import {
  findAllQuickAnswers,
  createQuickAnswer,
  updateQuickAnswer,
  deleteQuickAnswer,
  QuickAnswer,
  CreateQuickAnswerDto,
} from "../../services/incident.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import Loader from "../Loader.tsx";

const QuickAnswersManager = ({
  closeFn,
  setLoading,
}: {
  closeFn: () => void;
  setLoading: (value: boolean) => void;
}) => {
  const [quickAnswers, setQuickAnswers] = React.useState<QuickAnswer[]>([]);
  const [loading, setLocalLoading] = React.useState(false);
  const [newText, setNewText] = React.useState("");
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [editingText, setEditingText] = React.useState("");

  const fetchQuickAnswers = React.useCallback(async () => {
    try {
      setLocalLoading(true);
      const data = await findAllQuickAnswers();
      setQuickAnswers(data || []);
    } catch (error: any) {
      notification(
        "Error",
        error?.response?.data?.message || "Error al obtener respuestas rápidas",
        "danger"
      );
    } finally {
      setLocalLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchQuickAnswers();
  }, [fetchQuickAnswers]);

  const handleCreate = async () => {
    if (!newText.trim()) {
      notification("Error", "El texto no puede estar vacío", "danger");
      return;
    }

    try {
      setLoading(true);
      const data: CreateQuickAnswerDto = {
        text: newText.trim(),
        isActive: true,
      };
      await createQuickAnswer(data);
      notification("Éxito", "Respuesta rápida creada correctamente", "success");
      setNewText("");
      await fetchQuickAnswers();
    } catch (error: any) {
      notification(
        "Error",
        error?.response?.data?.message || "Error al crear respuesta rápida",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id: number) => {
    if (!editingText.trim()) {
      notification("Error", "El texto no puede estar vacío", "danger");
      return;
    }

    try {
      setLoading(true);
      await updateQuickAnswer(id, { text: editingText.trim() });
      notification(
        "Éxito",
        "Respuesta rápida actualizada correctamente",
        "success"
      );
      setEditingId(null);
      setEditingText("");
      await fetchQuickAnswers();
    } catch (error: any) {
      notification(
        "Error",
        error?.response?.data?.message ||
          "Error al actualizar respuesta rápida",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (
      !window.confirm(
        "¿Estás seguro de que deseas eliminar esta respuesta rápida?"
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await deleteQuickAnswer(id);
      notification(
        "Éxito",
        "Respuesta rápida eliminada correctamente",
        "success"
      );
      await fetchQuickAnswers();
    } catch (error: any) {
      notification(
        "Error",
        error?.response?.data?.message || "Error al eliminar respuesta rápida",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    try {
      setLoading(true);
      await updateQuickAnswer(id, { isActive: !currentStatus });
      notification(
        "Éxito",
        `Respuesta rápida ${
          !currentStatus ? "activada" : "desactivada"
        } correctamente`,
        "success"
      );
      await fetchQuickAnswers();
    } catch (error: any) {
      notification(
        "Error",
        error?.response?.data?.message || "Error al cambiar estado",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-3">
      <div className="mb-3">
        <h5
          style={{ marginBottom: "16px", color: "#374151", fontWeight: "600" }}
        >
          Respuestas Rápidas
        </h5>
        <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "16px" }}>
          Administra las respuestas rápidas que aparecerán como chips en las
          tablas de incidencias.
        </p>

        <div className="d-flex gap-2 mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Nueva respuesta rápida..."
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleCreate();
              }
            }}
            maxLength={200}
            style={{
              borderRadius: "8px",
              backgroundColor: "#f3f4f6",
              borderColor: "#d1d5db",
            }}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleCreate}
            style={{
              borderRadius: "8px",
              minWidth: "100px",
            }}
          >
            Agregar
          </button>
        </div>
      </div>

      <div style={{ maxHeight: "400px", overflowY: "auto" }}>
        {loading ? (
          <Loader isLoading={true} />
        ) : quickAnswers.length === 0 ? (
          <div className="text-center text-muted p-3">
            No hay respuestas rápidas configuradas
          </div>
        ) : (
          quickAnswers.map((qa) => (
            <div
              key={qa.id}
              className="border rounded p-2 mb-2 d-flex align-items-center justify-content-between"
              style={{
                borderRadius: "8px",
                backgroundColor: qa.isActive ? "white" : "#f8f9fa",
                opacity: qa.isActive ? 1 : 0.6,
              }}
            >
              {editingId === qa.id ? (
                <div
                  className="d-flex align-items-center gap-2"
                  style={{ flex: 1 }}
                >
                  <input
                    type="text"
                    className="form-control"
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    maxLength={200}
                    style={{
                      borderRadius: "6px",
                      fontSize: "13px",
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleUpdate(qa.id);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={() => handleUpdate(qa.id)}
                    style={{ borderRadius: "6px" }}
                  >
                    <i className="fa fa-check"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setEditingId(null);
                      setEditingText("");
                    }}
                    style={{ borderRadius: "6px" }}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ flex: 1, fontSize: "13px", color: "#374151" }}>
                    {qa.text}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={qa.isActive}
                        onChange={() => handleToggleActive(qa.id, qa.isActive)}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setEditingId(qa.id);
                        setEditingText(qa.text);
                      }}
                      style={{ borderRadius: "6px" }}
                      title="Editar"
                    >
                      <i className="fa fa-edit"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(qa.id)}
                      style={{ borderRadius: "6px" }}
                      title="Eliminar"
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default QuickAnswersManager;
